/**
 * Roles and scopes.
 *
 * ROLE KEY → DISPLAY LABEL. Keys are stored in grants, rows and code and NEVER change; labels are
 * what people see (owner decision D2). Five of them read differently from their key — watch
 * `hr_manager`, which displays as "HR Executive", while "HR Manager" is `hr_admin`; and
 * `travel_desk_agent`, which displays as "Travel Desk Admin" (ADR 0030 D3):
 *
 *   key                 label                  key                 label
 *   org_owner           Global Admin           employee            Employee
 *   hr_admin            HR Manager             auditor             Auditor
 *   hr_manager          HR Executive           it_admin            IT Admin
 *   manager             Reporting Manager      expense_manager     Expense Manager
 *   payroll_admin       Payroll Admin          performance_admin   Performance Admin
 *   finance             Finance                project_admin       Project Admin
 *   recruiter           Recruiter              asset_manager       Asset Manager
 *   helpdesk_manager    Helpdesk Manager       department_head     Department Head
 *   helpdesk_agent      Helpdesk Agent         travel_desk_manager Travel Desk Manager
 *   travel_desk_agent   Travel Desk Admin
 *
 * `ROLE_META` below is the single source of those labels: the API's per-org role seeding, the demo
 * seed and the deploy-time sync (packages/db/src/role-permissions-sql.ts) all read it.
 *
 * TWO INDEPENDENT AXES, and keeping them independent is the whole design:
 *
 *   ROLE  answers "what kind of thing may this person do?"   (approve leave, run payroll)
 *   SCOPE answers "over whom?"                               (themselves, their reports, one
 *                                                             legal entity, the whole org)
 *
 * A manager in Bangalore and an HR admin for the India entity may hold overlapping permissions
 * over completely different populations. Folding scope into the role name — `hr_admin_india`,
 * `manager_blr` — is how HRMS permission models become a hundred roles nobody can audit. Here a
 * grant is a (role, scope, scopeId) triple, so those two people hold the same `hr_admin` role with
 * different scopes.
 *
 * `platform_admin` is deliberately NOT in this file's model: platform staff live in the `platform`
 * schema with no RLS access to tenant tables, and reach a tenant only through an audited,
 * time-boxed impersonation grant. A role that could read every tenant's salary data is not a role
 * we want expressible here at all.
 */

import type { Permission } from './permissions.js';

// ---------------------------------------------------------------------------
// Scopes
// ---------------------------------------------------------------------------

/**
 * Ordered broadest → narrowest. The order is load-bearing: `scopeRank` uses it to decide which of
 * two grants is more permissive when explaining an authorisation decision.
 *
 * `reporting_tree` and `direct_reports` are RELATIVE scopes — they carry no `scopeId` because they
 * are computed against the actor's own position in the org chart at request time. That is why the
 * target passed to `can()` must carry the manager chain: authorisation for "my team" cannot be
 * answered from the grant alone.
 */
export const SCOPES = [
  'org',
  'legal_entity',
  'location',
  'department',
  'reporting_tree',
  'direct_reports',
  'self',
] as const;

export type Scope = (typeof SCOPES)[number];

/** Scopes that require a `scopeId` naming the specific entity/location/department. */
export const ABSOLUTE_SCOPES = ['legal_entity', 'location', 'department'] as const;

export function scopeRank(scope: Scope): number {
  return SCOPES.indexOf(scope);
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export const ROLES = [
  'org_owner',
  'hr_admin',
  'hr_manager',
  'payroll_admin',
  'finance',
  'recruiter',
  'manager',
  'employee',
  'auditor',
  'it_admin',
  // Narrow administrator roles (ADR 0023). Additive: finance and hr_admin keep everything they hold.
  'expense_manager',
  'performance_admin',
  'project_admin',
  'asset_manager',
  'department_head',
  // Helpdesk (ADR 0028). The agent is implicit: held by every member of a helpdesk team.
  'helpdesk_manager',
  'helpdesk_agent',
  // Travel Desk (ADR 0030). The agent is implicit: held by every member of a travel desk team, and
  // displays as "Travel Desk Admin".
  'travel_desk_manager',
  'travel_desk_agent',
] as const;

export type SystemRole = (typeof ROLES)[number];
export type Role = SystemRole | (string & {});

// ---------------------------------------------------------------------------
// Role → permission sets
// ---------------------------------------------------------------------------

/**
 * Every employee has these. Note how many are `.own` or naturally self-scoped: an employee's grant
 * is issued at scope `self`, so `leave:approve` being absent here is not what stops them approving
 * their own leave — the scope does. Both layers are deliberate.
 */
const EMPLOYEE: Permission[] = [
  'org:read',
  'legal_entity:read',
  'location:read',
  'department:read',
  'post:read',
  'employee:read',
  // The Employee Directory: every colleague's work-safe card (name, designation, team, work
  // email/phone, manager) — never the employee record `employee:read` guards.
  'employee.directory:read',
  'employment:read',
  'org_chart:read',
  'employee.document:read',
  // One's OWN pay, never anyone else's — not `compensation:read`, which is "other people's pay"
  // (D1, ADR 0023). Everything that spreads EMPLOYEE (manager, hr_manager, ...) inherits only this.
  'compensation.own:read',
  'attendance:read',
  'attendance:punch',
  'attendance.regularisation:request',
  'shift:read',
  'shift.change:request',
  'attendance.od:request',
  'attendance.early_exit_waiver:request',
  'timesheet:read',
  'timesheet:submit',
  'project:read',
  'project.task:read',
  'work_plan:read',
  'work_plan:submit',
  'leave:read',
  'leave:request',
  'leave:cancel',
  'leave.balance:read',
  'leave.policy:read',
  'leave.encashment:request',
  'leave.comp_off:request',
  'payslip.own:read',
  // Whether one's own salary was credited, and why not if it was not (ADR 0015).
  'payroll.payment.own:read',
  // Every employee elects their own tax regime and maintains their own declarations and proofs.
  // Self scope (ROLE_MAX_SCOPE below) is what confines it to their own record.
  'payroll.tax.own:manage',
  // Every employee declares their own FBP allocation. Self scope confines it, same as the tax
  // declaration above — a different permission because it is a different business process.
  'payroll.fbp.own:manage',
  // The employee viewport on Compliance & Taxes: their own PF/ESI/PT/TDS figures and their own
  // Form 16. Self scope confines it, exactly as it does the declaration above.
  'statutory.own:read',
  // Raising a query about one's own payslip, and following the thread on it.
  'payroll.query.own:manage',
  'expense:read',
  'expense:submit',
  'expense.advance:request',
  'travel:request',
  'payroll.loan:request',
  'payroll.ewa:request',
  'chat:read',
  'chat:send',
  'chat.channel:create',
  'workflow:read',
  'workflow.delegate:manage',
  'goal:read',
  'goal:create',
  'goal:edit:self',
  'goal:delete:self',
  'goal.checkin:create',
  'review:read',
  'review:submit',
  'review:self:submit',
  // Performance Reviews (Section 4): every employee reads/manages their own PIP if one exists —
  // `improvement_plan:manage` (creating one) is deliberately NOT here, only on MANAGER+, an
  // employee cannot open a PIP on themself.
  'improvement_plan:read',
  // Skills library (PRF-16): every employee views the skill catalogue and submits their own
  // self-rating (self-scope) — the manager-confirm/catalogue-authoring half is `skill:manage`,
  // MANAGER+ only.
  'skill:read',
  'feedback:give',
  // Feedback & 1:1s (Section 3): every employee can read feedback about themselves, ask peers for
  // 360° input, and both request and be scheduled into a 1:1 — the manager/HR elevations
  // (`feedback.identity:read`, broader-scope `feedback:read`/`one_on_one:*` via a wider grant) layer
  // on top of this same set through scope, not a different permission string.
  'feedback:read',
  'feedback.request:create',
  'one_on_one:read',
  'one_on_one:schedule',
  // Complete a task assigned to me on my own (or, for manager/HR/IT/finance, someone else's)
  // offboarding process — scope narrows this to "assigned to me" at the route, not here.
  'offboarding.task:complete',
  'learning:read',
  'policy:read',
  'policy:acknowledge',
  // Engage › Announcements (ADR 0029): the feed answers only for the caller's own audience.
  'announcement:read',
  'grievance:raise',
  'ticket:read',
  'ticket:create',
  'survey:read',
  'survey:respond',
  'recognition:give',
  'onboarding:read',
  'asset:read',
  'asset.request:create',
  'asset:maintenance:create',
  'ai.copilot:use',
  // The copilot may PROPOSE writes for an employee — "apply leave on the 14th" — never execute
  // them: every proposal waits for the person's own click, and then runs through the real route
  // with the employee's own grants (`leave:request` at self scope), so this widens nothing.
  'ai.agent.write:execute',
  'referral:read',
  'referral:create',
];

const MANAGER: Permission[] = [
  ...EMPLOYEE,
  'employee:export',
  // Their team's attendance (reporting_tree scope). Inherited by HR_MANAGER, HR_ADMIN and ORG_OWNER (P1-19).
  'attendance.team:read',
  'attendance:approve',
  'attendance.regularisation:approve',
  'shift.change:approve',
  'shift.roster:manage',
  'shift.rotation:manage',
  'attendance.od:approve',
  'attendance.early_exit_waiver:approve',
  'timesheet:approve',
  'overtime:approve',
  'project:create',
  'project:update',
  'project.member:manage',
  'project.task:manage',
  'project.milestone:manage',
  'project.analytics:read',
  'work_plan:approve',
  // Attest to the payroll inputs for their own reports, capped at reporting_tree scope. NOT an
  // approval of the run, and deliberately unaccompanied by any tax-declaration permission: a
  // manager has no business reading their report's rent receipts.
  'payroll.run.signoff:team',
  // The manager perspective on Reports & Accounts: aggregated cost for their own reporting line.
  // Capped at reporting_tree by ROLE_MAX_SCOPE, and the API narrows the response rather than
  // trusting the screen to hide the parts a manager may not see.
  'payroll.report.team:read',
  // Managers see their reports' salary queries, and cannot answer them: `payroll.query:resolve`
  // belongs to the payroll desk, which is the only place with the ledger to answer from.
  'payroll.query:read',
  // Payments (ADR 0015): whether a report was paid — never the amount or the account — and a REQUEST
  // to hold a report's salary. The request is prepared by payroll and approved by the owner; a
  // manager's request alone holds nothing.
  'payroll.payment.team:read',
  'payroll.hold:request',
  'leave:approve',
  // Cancels a report's leave for them, reporting_tree scope like leave:approve just above (Phase 4b).
  'leave.cancel:on_behalf',
  'expense:approve',
  // A manager can create an advance request for their own report, not only approve one the
  // report filed themselves — reporting_tree scope, same as expense:approve just above.
  'expense.advance:issue',
  // Same reasoning, same scope, for a claim (2026-09-28) — a manager files it in their report's
  // name, not their own.
  'expense.claim:file_for',
  // Travel requests (R-P10) are approved through the workflow step, same as expense:approve
  // above; `travel:approve` itself is what lets a manager view a report's requests and act as the
  // travel desk (record a booking) — see travel-request.contract.ts's own header.
  'travel:approve',
  'payroll.loan:recommend',
  'workflow.instance:approve',
  'goal:manage',
  'goal:edit:team',
  'goal:approve',
  'review.calibrate:execute',
  // A manager rates their reports' competencies and decides a PIP — see permissions.ts's header
  // comment on why `review.manager:submit` is a distinct permission from `review:submit`.
  'review.manager:submit',
  'improvement_plan:manage',
  // Skills library (PRF-16): a manager confirms a report's self-rating, capped at reporting_tree by
  // ROLE_MAX_SCOPE below, same shape as review.manager:submit.
  'skill:manage',
  // Sees direct reports' offboarding processes and their own assigned tasks (handover sign-off,
  // MANAGER-role clearance) — capped at direct_reports by ROLE_MAX_SCOPE, nothing org-wide.
  'offboarding.team:read',
  'learning.assign:execute',
  'requisition:read',
  'requisition:create',
  'requisition:update',
  'candidate:read',
  'interview:read',
  'interview:schedule',
  'interview.feedback:submit',
  'report:read',
  // Performance Insights (plan Phase 3): a manager sees cohort analytics over their own reporting
  // tree, capped there by ROLE_MAX_SCOPE below — that cap is what keeps `:export` here from ever
  // reaching an org-wide download, so there's no reason to withhold it: the plan's own role table
  // ("MANAGER: own team only (reporting_tree scope) + export") and permission list both explicitly
  // include it, closing the real gap the mockup exposed (MANAGER never held the org-wide
  // `report:export`, so its own "Download Appraisal Summary" button had nothing to call until this
  // narrower permission existed).
  'analytics.performance:read',
  'analytics.performance:export',
  'asset:maintenance:read',
  'ai.agent.write:execute',
  // Merit (decision Q1, perf remediation N1): a manager recommends increments for their own tree —
  // views the worksheet and submits "their %" — capped at reporting_tree by ROLE_MAX_SCOPE.
  // Deciding the cycle (`merit:approve`/`:reject`) and authoring the matrix (`:manage`) are HR's.
  // DEPARTMENT_HEAD inherits both at department scope. HR_MANAGER does NOT: see its own note.
  'merit:read',
  'merit:submit',
];

const HR_MANAGER: Permission[] = [
  // Every manager permission EXCEPT merit (decision Q1): the HR Executive has no access to
  // financials, and a merit worksheet carries each report's current pay. Filtered here rather
  // than left off MANAGER, so the manager and department head keep their own recommendations.
  ...MANAGER.filter((p) => !p.startsWith('merit:')),
  'employee:create',
  'employee:update',
  'employee.document:manage',
  'employment:create',
  'employment:update',
  'org_chart:update',
  'attendance:update',
  'shift:manage',
  'shift.roster:manage',
  'leave.balance:adjust',
  'leave.year_end:read',
  'leave.report:read',
  'leave.plan:read',
  'onboarding:manage',
  'offboarding:read',
  // Records an employee's exit (last working day, exit type) within the grant's scope. The exit then
  // runs through its approval workflow; HR Manager keeps `offboarding:manage` for starting processes.
  'employment.exit:execute',
  // Records exit-interview answers on the employee's behalf (HR-conducted interviews) — the
  // employee's own self-serve submission uses no permission at all (routeMeta(null) + self).
  'exit_interview:conduct',
  'asset:manage',
  'asset:create',
  'asset:edit',
  'asset:assign',
  'asset:transfer',
  'asset:return',
  'asset:maintenance:manage',
  'asset.request:approve',
  'asset.category:manage',
  // Manage without read is incoherent: this role must be able to list/see the categories it can
  // manage, not just call the write endpoints blind. asset.type:manage/asset.vendor:* stay with
  // hr_admin only — this is deliberately just the read half paired with the manage grant above.
  'asset.category:read',
  'asset.batch:read',
  'review:read',
  // Performance Reviews (Section 4): reassign/reopen an individual review, cross-team, without the
  // org-wide `review.cycle:manage` that hr_admin alone holds — see the plan's role table.
  'review:manage',
  // Review appeals (PRF-10) — an HR-side function like the grant above: every hr_manager can see
  // the appeal queue and decide/reopen appeals at this role's own max scope. The seeded
  // review_appeal workflow routes its step to the hr_manager whose department grant covers the
  // employee, falling back to hr_admin (decision Q15).
  'review:appeal:read',
  'review:appeal:approve',
  'review:appeal:reject',
  'review:appeal:manage',
  // Training (plan §8, decision Q4): library CRUD, content upload, policy, org monitor and reopen,
  // reached at this role's department max scope (SYSTEM_ROLE_MAX_SCOPE below) — never
  // `learning.override:execute` or `learning.recording:view`, which stay hr_admin-only (D10).
  'learning:manage',
  'ticket:manage',
  'recognition:manage',
  // Every trip in scope and who is travelling (ADR 0030) — reach is this role's department cap.
  'travel:read',
  'analytics.attrition:read',
  // Performance Insights: both `analytics.performance:read` and `:export` are already inherited
  // from MANAGER above — this role just reaches them at its own wider (department) max scope.
];

const HR_ADMIN: Permission[] = [
  ...HR_MANAGER,
  'org.settings:read',
  'org.settings:update',
  // The billing module's screens are explicitly "Company Owner & HR Admin Only" — HR admin
  // manages the subscription, submits checkout, and uploads an offline-payment proof. Verifying
  // that proof is NOT a tenant permission at all: it is decided by Vanguard HR's own platform
  // staff against their own bank statement (see OfflinePaymentProof's doc comment in
  // schema.prisma) — a tenant role confirming its own payment would be self-attestation.
  'org.billing:read',
  'org.billing:update',
  'legal_entity:read',
  'location:manage',
  'department:manage',
  'post:manage',
  'user:read',
  'user:create',
  'user:update',
  'user:deactivate',
  'role:read',
  'role:assign',
  'employee:delete',
  'employee.pii:read',
  // Full (decrypted) PAN, Aadhaar and account numbers in the export — every use is audited.
  'employee.pii:export',
  // De-anonymises feedback authorship for abuse/POSH investigations — see feedback.contract.ts.
  'feedback.identity:read',
  'employment.transfer:execute',
  'employment.exit:execute',
  'leave.policy:manage',
  'leave.year_end:execute',
  'leave.plan:manage',
  'holiday_calendar:manage',
  'attendance.cycle:freeze',
  'attendance.penalty:manage',
  'attendance.policy:manage',
  'project:archive',
  'work_form:manage',
  'clock_out_policy:manage',
  'biometric_device:manage',
  // Same audience the DLP-flag notification already reaches (CHT-14) — reviewing what it flagged
  // belongs to whoever gets told about it, not a separate compliance-only role.
  'chat.dlp:review',
  // Org-wide chat configuration (edit window, link previews, presence, archive visibility) — the
  // same audience as the DLP review queue; the owner inherits it through HR_ADMIN.
  'chat.settings:manage',
  'offboarding:manage',
  // Edits OffboardingPolicy (notice period, auto-archive, reminders, ...) and ClearanceFunction —
  // the offboarding Settings screen. Org-wide configuration, hr_admin only.
  'offboarding.policy:manage',
  // Full & Final Settlement lives under the Employee admin section, not a separate Payroll one
  // (that section is still a placeholder — see ADMIN_NAV). hr_admin needs both: `payroll:read` to
  // view settlements, `fnf:execute` to create one and mark it paid. The approval STEP itself
  // routes to hr_admin too — see the `fnf_settlement` WorkflowDefinition — for the same reason
  // `employee_exit` already does: HR, not a payroll-specific role, is the approver every org is
  // guaranteed to have configured.
  'payroll:read',
  'fnf:execute',
  // The Payroll F&F screen (ADR 0015) reads settlements through `fnf:read`, and HR defines which
  // departments clear an exit. Approving a settlement is the owner's (`fnf:approve`), not HR's.
  'fnf:read',
  'fnf.clearance:manage',
  // HR keeps the yearly pay calendar and proposes salary component changes; defining pay-group
  // rules and approving component changes stay with the owner (the "dual review").
  'payroll.rules:read',
  'payroll.calendar:maintain',
  'payroll.component:propose',
  // HR runs proof verification. `payroll.tax.own:manage` is already inherited from EMPLOYEE — an
  // HR admin maintains their OWN declaration through self-service like anyone else, and cannot
  // verify it, because can() refuses `payroll.tax.proof:verify` against one's own record.
  'payroll.tax:read',
  'payroll.tax.proof:verify',
  // HR staffs the salary-query desk with payroll. Answering is `payroll.query:resolve`; scheduling
  // an adjustment into a run still needs payroll's own permissions on that run.
  'payroll.query:read',
  'payroll.query:resolve',
  'compensation:read',
  // Merit (decision Q1): the HR Manager runs the whole merit cycle — matrix and budget, every
  // worksheet org-wide, and the merit_cycle_budget approval step (Q15). Listed in full because
  // HR_MANAGER above filters merit out of what this array inherits. The org owner inherits all five
  // through ...HR_ADMIN; performance_admin holds none (it reaches no one's pay).
  'merit:read',
  'merit:submit',
  'merit:approve',
  'merit:reject',
  'merit:manage',
  'workflow:manage',
  'review.cycle:manage',
  'competency:manage',
  'skill:manage',
  'learning:manage',
  // Release a clock-out block / waive a webcam requirement, and revoke a certificate. HR admin
  // only — self-excluded (SELF_EXCLUDED_PERMISSIONS in can.ts), so nobody releases their own.
  'learning.override:execute',
  // View training webcam footage (access-logged). Not granted to HR_MANAGER or MANAGER (D10) —
  // an HR manager reaches `learning:manage` at department scope but never sees recordings.
  'learning.recording:view',
  'policy:manage',
  // ADDED 2026-09-22 — every other policy screen HR Admin owns (leave policy, holiday calendar)
  // already lives here; expense categories was the one exception, gated on `finance`/`org_owner`
  // only. `expense:read` needed to actually see the catalog is already inherited from EMPLOYEE.
  'expense.policy:manage',
  // Flat permission model — `:manage` does not imply `:read`, and the screen now gates on `:read`.
  'expense.policy:read',
  // HR can issue an advance for anyone in the org, not just a manager for their own reports.
  'expense.advance:issue',
  // Same reasoning, org scope (2026-09-28) — HR can file a claim for anyone in the org.
  'expense.claim:file_for',
  // Org-wide communication, like the handbook (ADR 0029); the owner inherits it through HR_ADMIN.
  'announcement:manage',
  'compliance:read',
  'compliance:manage',
  'posh.case:read',
  'posh.case:manage',
  'grievance:manage',
  'dsr:manage',
  'survey:manage',
  'report:create',
  'report:export',
  'chat.channel:manage',
  'chat.announcement:broadcast',
  'import:execute',
  'audit:read',
  'ai.audit:read',
  // HR Head is the final approval step for both requisitions and offers (see the seeded
  // job_requisition/offer WorkflowDefinitions) and can manage the recruitment pipeline directly,
  // not just approve into it.
  'requisition:update',
  'requisition:close',
  'requisition:approve',
  'candidate:manage',
  'interview:read',
  'offer:create',
  'offer:send',
  'offer:approve',
  'offer:rescind',
  // Approvers of the offer workflow see the figure they approve.
  'offer.compensation:read',
  'recruitment.analytics:read',
  // Settings -> Integrations (Google Workspace / Microsoft 365 calendar connect) is HR's tool to
  // configure, not IT's — see roles.ts's IT_ADMIN for the other holder of these two permissions.
  'integration:read',
  'integration:manage',
  // Connected applications (ADR 0033): a STARTING SET only — each company re-assigns these in
  // Settings -> Roles. HR sees status and syncs people; connecting an application (which sends
  // personal data out of the company) starts with the Global Admin (ORG_OWNER below).
  'app_integration:read',
  'app_integration:sync',
  // Notifications: HR/admin decides everything, per module × channel; employees configure nothing,
  // so neither MANAGER nor EMPLOYEE above holds any of these three. org_owner inherits them through
  // this array, same as every other HR_ADMIN grant.
  'notification.config:read',
  'notification.config:manage',
  'notification.delivery:read',
  // Asset Configuration & Vendors (Administration): full manage on top of the `asset.category:manage`
  // already inherited from HR_MANAGER above — see permissions.ts's "Asset Configuration & Vendors"
  // block for why these are separate from the asset-register permissions.
  'asset.category:read',
  'asset.type:read',
  'asset.type:manage',
  'asset.vendor:read',
  'asset.vendor:manage',
  'asset:export',
  'asset.batch:read',
  'asset.batch:manage',
  'asset:delete',
  'asset:retire',
  'asset:dispose',
  'asset:audit:read',
  'asset:audit:create',
  'asset:audit:verify',
  // The role that opens and verifies an audit run also progresses and closes it; without these two
  // nobody could finish a run (the API refuses a close without `asset:audit:close`).
  'asset:audit:manage',
  'asset:audit:close',
  'asset:analytics:read',
  'asset:analytics:export',
  // Helpdesk Manager's permissions (ADR 0028): the HR Manager runs the helpdesk unless an
  // organisation hands it to a dedicated Helpdesk Manager.
  'ticket.all:manage',
  'ticket.config:manage',
  'ticket.report:read',
  // Travel Desk (ADR 0030): HR sets travel policy, same as the Expense Manager. `travel:read` and
  // `travel:approve` both come from `...HR_MANAGER` above (which itself spreads `...MANAGER`), the
  // same inheritance that already gives HR everything else a manager holds — not granted directly
  // here.
  'travel.policy:manage',
];

const PAYROLL_ADMIN: Permission[] = [
  ...EMPLOYEE,
  'employee:read',
  'employee.pii:read',
  'employment:read',
  'compensation:read',
  'compensation:update',
  'compensation.cycle:manage',
  'attendance:read',
  // Payroll reads everyone's attendance to compute loss of pay (P1-19).
  'attendance.team:read',
  'leave:read',
  'payroll:read',
  'payroll.run:create',
  'payroll.run:execute',
  'payroll.input:manage',
  'payroll.rules:read',
  'payroll.rules:manage',
  'payroll.rules.override:manage',
  'payroll.calendar:maintain',
  'payroll.component:propose',
  'payroll.tax:read',
  'payroll.tax.proof:verify',
  'payroll.tax.config:manage',
  'payslip:read',
  'payslip:publish',
  'bank_file:generate',
  // The MAKER on statutory returns: generate the file, record what the portal said. Signing it off
  // (`statutory.filing:approve`) and releasing the challan (`statutory.remittance:approve`) are
  // deliberately absent — a payroll admin who held all three would be both sides of the control.
  'statutory.filing:manage',
  'form16:generate',
  'form16:issue',
  // Prepares and posts the payroll journal. Closing the period is finance's and the owner's call.
  'payroll.gl:manage',
  'payroll.query:read',
  'payroll.query:resolve',
  'loan:manage',
  'fnf:execute',
  'fnf:read',
  // The payroll desk's half of payments (ADR 0015): prepare a batch, and send it once someone else
  // has authorised it. Authorising (`payroll.payment:authorise`) and recording an outcome the system
  // did not see (`payroll.payment:reconcile`) are deliberately absent.
  'payroll.payment:read',
  'payroll.payment:prepare',
  'payroll.payment:transmit',
  // Holds and off-cycle payments are prepared here and approved by the owner.
  'payroll.hold:request',
  'payroll.hold:manage',
  'payroll.offcycle:manage',
  'expense:read',
  'report:read',
  'report:export',
  'analytics.compensation:read',
  'import:execute',
  'ai.agent.write:execute',
];

const FINANCE: Permission[] = [
  ...EMPLOYEE,
  'payroll:read',
  'payroll.run:approve',
  // The lock and the release that follow the approval this role already holds (ADR 0014). Held by no
  // payroll-operations role: the people who prepare and submit a run do not also finalize or release it.
  'payroll.run:finalize',
  'payroll.run:release',
  'payslip:read',
  'bank_file:generate',
  // Finance reads payments and reconciles outcomes against the bank statement (ADR 0015). It neither
  // prepares nor authorises a batch: the owner authorises, and the payroll desk prepares and sends.
  'payroll.payment:read',
  'payroll.payment:reconcile',
  'fnf:read',
  // Finance releases the statutory challan and closes the accounting period. It does NOT hold
  // `statutory.filing:manage`: the file it is releasing money against was prepared by someone else.
  'statutory.remittance:approve',
  'payroll.gl:manage',
  'payroll.period:close',
  'expense:read',
  'expense:approve',
  'expense:settle',
  'expense.policy:manage',
  'expense.policy:read',
  'expense.advance:manage',
  'travel:approve',
  // Reads every trip for advances and settlement (ADR 0030); not `travel.policy:manage`, which is
  // the Expense Manager's and HR's.
  'travel:read',
  'compensation:read',
  'analytics.compensation:read',
  'project.analytics:read',
  'report:read',
  'report:create',
  'report:export',
  'org.billing:read',
  // Legal Agreement / E-Signature (Phase A): Finance reads the org's own agreement status/history at
  // the same tier as org.billing:read above — sending/creating one stays with the owner alone.
  'agreement:read',
  'workflow.instance:approve',
  // Finance is the budget-controller step in the requisition approval chain (see the seeded
  // job_requisition WorkflowDefinition) and cares about cost-per-hire. `requisition:read` has to
  // come with `:approve` — without it, `listRequisitions`/`getRequisition` filter every row out
  // before the approve check ever runs (see recruitment.ts's `can(actor, 'requisition:read', ...)`
  // gates), so Finance could be assigned the workflow step and still see zero rows to act on.
  'requisition:read',
  'requisition:approve',
  'recruitment.analytics:read',
  'asset:read',
  'asset.financial:read',
  // Asset Configuration & Vendors: read-only on the category/type taxonomy, full manage on the
  // vendor directory — Finance owns vendor payment/contract terms, not asset classification.
  'asset.category:read',
  'asset.type:read',
  'asset.vendor:read',
  'asset.vendor:manage',
  'asset:export',
  'asset.batch:read',
  'asset.batch:manage',
  'asset:financial:manage',
  'asset:analytics:read',
  'asset:analytics:export',
];

const RECRUITER: Permission[] = [
  ...EMPLOYEE,
  // No `compensation:read`: hiring never needs an employee's pay (offers carry their own figures),
  // so a recruiter sees only their own salary, like everyone else. An org that wants recruiters to
  // see pay switches it on for the role on Roles & Permissions (D1, ADR 0023).
  'requisition:read',
  'requisition:create',
  'requisition:update',
  'requisition:close',
  'candidate:read',
  'candidate:manage',
  'interview:read',
  'interview:schedule',
  'interview.feedback:submit',
  'offer:create',
  'offer:send',
  'offer:rescind',
  // The recruiter writes the offer, so sees its CTC. An org with junior recruiters who prepare
  // offers but must not see others' pay removes this from their role (bug 14).
  'offer.compensation:read',
  'recruitment.analytics:read',
  'onboarding:manage',
  'report:read',
  'ai.agent.write:execute',
];

/** Read-only across the org, including the audit trail. Never a write permission — by design. */
const AUDITOR: Permission[] = [
  'org:read',
  'legal_entity:read',
  'location:read',
  'department:read',
  'post:read',
  'employee:read',
  'employment:read',
  'org_chart:read',
  'compensation:read',
  // The auditor does not spread EMPLOYEE, so its own-pay permission is listed here.
  'compensation.own:read',
  'attendance:read',
  'attendance.team:read',
  'leave:read',
  'leave.balance:read',
  'leave.policy:read',
  'leave.plan:read',
  'payroll:read',
  'payslip:read',
  'payroll.payment:read',
  'fnf:read',
  'payroll.rules:read',
  // Reads the statutory filings, the journal and the query desk; signs off none of them.
  'payroll.query:read',
  'expense:read',
  // The category catalog, read-only — the admin screen gates on this (`:read`) so an auditor can
  // find it, the same way every other admin section gates on a `:read` permission (`nav.ts`).
  'expense.policy:read',
  'policy:read',
  'compliance:read',
  'workflow:read',
  'project:read',
  'project.task:read',
  'project.analytics:read',
  'work_plan:read',
  // PM_FINAL_SCOPE_SPECIFICATION.md Part 1/7: the one confirmed read gap against this role's
  // stated "read-only across the org, including the audit trail" purpose. work_plan:read above
  // already covers Work Forms — no separate work_form:read permission is added for this.
  'timesheet:read',
  'report:read',
  'report:export',
  'audit:read',
  'audit:export',
  'ai.audit:read',
  'analytics.compensation:read',
  'analytics.attrition:read',
  // Performance Insights: read-only, same reasoning as the other analytics.* permissions above —
  // never `:export`, per this role's entire purpose.
  'analytics.performance:read',
  // Read-only across Feedback & 1:1s too, including de-anonymised authorship — see the role table
  // in the Feedback & 1:1s plan: the auditor is the one non-HR role that also gets identity:read.
  'feedback:read',
  'feedback.identity:read',
  'one_on_one:read',
  // Performance Reviews (Section 4): read-only across reviews and PIPs too, same reasoning as
  // feedback:read above — an appraisal dispute audit needs the auditor to see the record.
  'review:read',
  'improvement_plan:read',
  'asset:read',
  // Asset Configuration & Vendors: read-only, never `:manage` — `:export` IS granted though,
  // same reasoning as `audit:export`/`report:export` above: exporting records for a compliance
  // check is exactly this role's purpose.
  'asset.category:read',
  'asset.type:read',
  'asset.vendor:read',
  'asset:export',
  'asset.batch:read',
  'asset:audit:read',
  'asset:analytics:read',
];

const IT_ADMIN: Permission[] = [
  ...EMPLOYEE,
  // No `compensation:read`, for the same reason as RECRUITER: running SSO and integrations never
  // needs anyone's pay. Switchable per org on Roles & Permissions (D1, ADR 0023).
  'user:read',
  'user:create',
  'user:update',
  'user:deactivate',
  'role:read',
  'sso:manage',
  'apikey:manage',
  'integration:read',
  'integration:manage',
  'webhook:manage',
  'asset:manage',
  'asset:create',
  'asset:edit',
  'asset:assign',
  'asset:transfer',
  'asset:return',
  'asset:retire',
  'asset:dispose',
  'asset.request:approve',
  'asset.category:manage',
  'asset:maintenance:read',
  'asset:maintenance:manage',
  'asset.maintenance:cost_input',
  'asset:audit:read',
  'asset:audit:create',
  'asset:audit:verify',
  // IT runs the physical audit end to end: open a run, progress it, close it. IT still holds no
  // financial read — charging an employee for an unreturned asset stays with Finance.
  'asset:audit:manage',
  'asset:audit:close',
  // Asset Configuration & Vendors: category manage is held; type/vendor stay read-only for IT.
  'asset.category:read',
  'asset.type:read',
  'asset.vendor:read',
  'asset:export',
  'asset.batch:read',
  'asset.batch:manage',
  'ticket:manage',
  'audit:read',
];

// ---------------------------------------------------------------------------
// Narrow administrator roles (ADR 0023)
// ---------------------------------------------------------------------------
// Each carves ONE module's administration out of a broad role, so an organisation can hand "run
// expenses" or "run the review cycle" to someone without also handing them HR or finance. They are
// additive: the broad roles they are carved from keep every permission they had.

/**
 * Expense and travel administration: approve, settle and set policy. Carved from FINANCE, plus
 * travel policy (ADR 0030), which finance does not hold.
 */
const EXPENSE_MANAGER: Permission[] = [
  ...EMPLOYEE,
  'expense:read',
  'expense:approve',
  'expense:settle',
  // EXP-P1-17 — held `:manage` without `:read`, but listPolicies/getPolicy/getExpenseSettings all
  // require `:read` (same as FINANCE, which already holds both, right next to each other). A role
  // that can manage policies but cannot read them is not a narrower grant, it's a broken one —
  // "Policies & Categories" was visible in the admin nav (gated on `:manage`) but 403'd the moment
  // it loaded.
  'expense.policy:manage',
  'expense.policy:read',
  'travel:approve',
  'travel:read',
  'travel.policy:manage',
  // Expense claims and travel requests are approved through @hrms/workflow steps.
  'workflow.instance:approve',
];

/** Review cycles, calibration, competencies, goal templates and Performance Insights. Carved from HR_ADMIN. */
const PERFORMANCE_ADMIN: Permission[] = [
  ...EMPLOYEE,
  'review:read',
  'review:manage',
  'review.cycle:manage',
  'review.calibrate:execute',
  // Review appeals (PRF-10) — this role runs reviews end-to-end, so it holds the same appeal
  // queue access review:manage's own carve-out grants HR_MANAGER.
  'review:appeal:read',
  'review:appeal:approve',
  'review:appeal:reject',
  'review:appeal:manage',
  'competency:manage',
  'skill:manage',
  // Goal templates and company/department objectives are gated on `goal:manage`.
  'goal:manage',
  'goal:approve',
  'goal:edit:team',
  'improvement_plan:read',
  'analytics.performance:read',
  'analytics.performance:export',
];

/** Org-wide project, timesheet and work-plan administration. Carved from HR_ADMIN + MANAGER. */
const PROJECT_ADMIN: Permission[] = [
  ...EMPLOYEE,
  'project:create',
  'project:update',
  'project:archive',
  'project.member:manage',
  'project.task:manage',
  'project.milestone:manage',
  'project.analytics:read',
  'timesheet:approve',
  'work_plan:approve',
  'work_form:manage',
];

/**
 * The asset register: exactly the `asset*` permissions HR_MANAGER and IT_ADMIN hold, derived rather
 * than copied so the two cannot drift apart.
 */
const ASSET_MANAGER: Permission[] = [
  ...new Set<Permission>([
    ...EMPLOYEE,
    ...[...HR_MANAGER, ...IT_ADMIN].filter((p) => p.startsWith('asset')),
  ]),
];

/**
 * A manager's permission set, reaching a whole department (ROLE_MAX_SCOPE). Granted automatically
 * from the org chart to the head of a department, not assigned by hand.
 */
const DEPARTMENT_HEAD: Permission[] = [...MANAGER];

/**
 * The helpdesk (ADR 0028). The manager sees and works every ticket — confidential categories
 * included — configures categories, teams, SLA targets and hours, and reads the reports. Nothing
 * here reaches anyone's pay (ADR 0023): a ticket is text an employee wrote, never a salary view.
 */
const HELPDESK_MANAGER: Permission[] = [
  ...EMPLOYEE,
  'ticket:manage',
  'ticket.all:manage',
  'ticket.config:manage',
  'ticket.report:read',
];

/**
 * Held implicitly by every member of a helpdesk team (implicit.ts), never granted by hand. WHICH
 * tickets an agent works is their teams' categories — decided by the API from membership, not by
 * this grant's scope.
 */
const HELPDESK_AGENT: Permission[] = [...EMPLOYEE, 'ticket:manage'];

/**
 * The travel desk (ADR 0030, D3). The manager runs the desk: teams, members, coverage, vendors,
 * reassignment, every queue, and reads every trip. Neither desk role settles money, sets travel
 * policy or overrides a travel decision, and nothing here reaches anyone's pay (ADR 0023).
 */
const TRAVEL_DESK_MANAGER: Permission[] = [
  ...EMPLOYEE,
  'travel:read',
  'travel.desk:read',
  'travel.desk:book',
  'travel.desk:manage',
];

/**
 * Held implicitly by every member of a travel desk team (implicit.ts), never granted by hand; shown
 * as "Travel Desk Admin". WHICH queues an agent reads and books is their teams' coverage — decided
 * by the API from membership, not by this grant's scope. No `travel:read`: an agent sees the trips
 * in their queues, not every trip.
 */
const TRAVEL_DESK_AGENT: Permission[] = [...EMPLOYEE, 'travel.desk:read', 'travel.desk:book'];

/**
 * Everything except impersonation, which is granted explicitly and separately even to the owner:
 * "log in as this employee" is the one action whose blast radius justifies a deliberate,
 * time-boxed, separately-audited grant rather than inheritance from a role.
 */
const ORG_OWNER: Permission[] = [
  ...new Set<Permission>([
    ...HR_ADMIN,
    ...PAYROLL_ADMIN,
    ...FINANCE,
    ...RECRUITER,
    ...IT_ADMIN,
    ...AUDITOR,
    ...EXPENSE_MANAGER,
    ...PERFORMANCE_ADMIN,
    ...PROJECT_ADMIN,
    ...ASSET_MANAGER,
    ...DEPARTMENT_HEAD,
    ...HELPDESK_MANAGER,
    ...TRAVEL_DESK_MANAGER,
    'org:update',
    'org.branding:update',
    'org.billing:read',
    'org.billing:update',
    // Salary PIN (ADR 0041): the way out for someone who cannot reset their own PIN by phone. Owner-only:
    // whoever holds it can make a payroll user re-create their PIN, so it stays with the account's owner.
    'salary_pin:reset',
    // Company groups (ADR 0034). A starting set only: each company can grant these to another role
    // on Roles & Permissions. Managing access additionally requires being a group owner.
    'org.company:create',
    'org.group:manage',
    // Group learning (ADR 0036): the all-companies training library and calendar. A starting set only,
    // like the two above. Also needs a group owner who manages training company-wide in each company.
    'learning.group:manage',
    // Legal Agreement / E-Signature (Phase A): creating/sending the org's MSA/DPA is owner-only,
    // same tier as org.billing:update above. `agreement:read` is already inherited via ...FINANCE.
    'agreement:manage',
    'legal_entity:create',
    'legal_entity:update',
    'payroll.run:approve',
    'payroll.run:reopen',
    'payroll.paygroup:manage',
    'payroll.component:approve',
    // The publisher half of rule changes: payroll administrators draft, the owner puts them live.
    // Held by no other built-in role — author and publisher must be different people.
    'payroll.rules:publish',
    // Switching an entity onto the rule engine changes what people are paid. Held by no other built-in
    // role, and never implied by `payroll.rules:manage`.
    'payroll.rules:rollout',
    // The super-admin sign-off the Compliance screen asks for: a named officer authorising a
    // statutory return prepared by payroll, and releasing the money against it.
    'statutory.filing:approve',
    'statutory.remittance:approve',
    'payroll.period:close',
    // The Super Admin's money decisions (ADR 0015, owner decision 2026-09-22): authorising a payment
    // batch, putting a salary hold into effect, approving an off-cycle payment and a Full & Final
    // settlement. Held by no other built-in role. The owner also inherits the payroll desk's
    // `payroll.payment:prepare`, so the API refuses the preparer of a batch version its authorisation.
    'payroll.payment:authorise',
    'payroll.hold:approve',
    'payroll.offcycle:approve',
    'payslip.policy:manage',
    'fnf:approve',
    'offer:approve',
    'requisition:approve',
    'chat.message:delete_any',
    'chat.export:execute',
    'chat.import:execute',
    'chat.legal_hold:manage',
    'ai.settings:manage',
    // Connecting an outside application sends employee data out of the company: the starting set
    // gives it to the Global Admin, who can grant it to any role in Settings -> Roles.
    'app_integration:manage',
  ]),
];

// Typed `Record<SystemRole, …>` first, so adding a key to ROLES without a permission set, a max
// scope or a label is a type error rather than a role that silently falls back to nothing.
const SYSTEM_ROLE_PERMISSIONS: Record<SystemRole, ReadonlySet<Permission>> = {
  employee: new Set(EMPLOYEE),
  manager: new Set(MANAGER),
  hr_manager: new Set(HR_MANAGER),
  hr_admin: new Set(HR_ADMIN),
  payroll_admin: new Set(PAYROLL_ADMIN),
  finance: new Set(FINANCE),
  recruiter: new Set(RECRUITER),
  auditor: new Set(AUDITOR),
  it_admin: new Set(IT_ADMIN),
  org_owner: new Set(ORG_OWNER),
  expense_manager: new Set(EXPENSE_MANAGER),
  performance_admin: new Set(PERFORMANCE_ADMIN),
  project_admin: new Set(PROJECT_ADMIN),
  asset_manager: new Set(ASSET_MANAGER),
  department_head: new Set(DEPARTMENT_HEAD),
  helpdesk_manager: new Set(HELPDESK_MANAGER),
  helpdesk_agent: new Set(HELPDESK_AGENT),
  travel_desk_manager: new Set(TRAVEL_DESK_MANAGER),
  travel_desk_agent: new Set(TRAVEL_DESK_AGENT),
};

export const ROLE_PERMISSIONS: Readonly<Record<Role, ReadonlySet<Permission>>> =
  Object.freeze(SYSTEM_ROLE_PERMISSIONS);

/**
 * The widest scope each role may be granted at.
 *
 * A cap, not a default. It exists so a misconfigured grant cannot quietly hand an `employee` role
 * org-wide reach — the seeder and the role-assignment endpoint both validate against it.
 */
const SYSTEM_ROLE_MAX_SCOPE: Record<SystemRole, Scope> = {
  employee: 'self',
  manager: 'reporting_tree',
  hr_manager: 'department',
  hr_admin: 'org',
  payroll_admin: 'org',
  finance: 'org',
  recruiter: 'org',
  auditor: 'org',
  it_admin: 'org',
  org_owner: 'org',
  expense_manager: 'org',
  performance_admin: 'org',
  project_admin: 'org',
  asset_manager: 'org',
  department_head: 'department',
  helpdesk_manager: 'org',
  helpdesk_agent: 'org',
  travel_desk_manager: 'org',
  travel_desk_agent: 'org',
};

export const ROLE_MAX_SCOPE: Readonly<Record<Role, Scope>> = Object.freeze(SYSTEM_ROLE_MAX_SCOPE);

export interface RoleMeta {
  readonly label: string;
  readonly description: string;
}

/**
 * The DEFAULT display label and description of each system role — see the key → label table at the
 * top of this file. An organisation may rename its own copy (its `roles` row); this is what a new
 * organisation starts with, and what the deploy-time sync gives an existing one a new role with.
 */
export const ROLE_META: Readonly<Record<SystemRole, RoleMeta>> = Object.freeze({
  org_owner: {
    label: 'Global Admin',
    description: 'Full organisation administration and tenant ownership',
  },
  hr_admin: {
    label: 'HR Manager',
    description: 'Manage employees, HR operations, payroll configuration and organisation settings',
  },
  hr_manager: {
    label: 'HR Executive',
    description: 'Departmental HR lifecycle management and onboarding',
  },
  payroll_admin: {
    label: 'Payroll Admin',
    description: 'Manage salary runs, tax computations and disbursement preparation',
  },
  finance: {
    label: 'Finance',
    description: 'Payroll approval, payment reconciliation, expense settlement and reporting',
  },
  recruiter: {
    label: 'Recruiter',
    description: 'Candidate sourcing, offer rollouts and interview scheduling',
  },
  manager: {
    label: 'Reporting Manager',
    description: 'Approvals and team operations for the people who report to them',
  },
  employee: {
    label: 'Employee',
    description: 'Self-service for profile, attendance, leave and their own pay',
  },
  auditor: {
    label: 'Auditor',
    description: 'Read-only audit logging and compliance oversight',
  },
  it_admin: {
    label: 'IT Admin',
    description: 'System integrations, SSO management and API access',
  },
  expense_manager: {
    label: 'Expense Manager',
    description:
      'Approve and settle expense claims and travel requests, and set expense and travel policy',
  },
  performance_admin: {
    label: 'Performance Admin',
    description:
      'Run review cycles and calibration, manage competencies and goal templates, read Performance Insights',
  },
  project_admin: {
    label: 'Project Admin',
    description: 'Administer every project, the timesheet register and work-plan forms',
  },
  asset_manager: {
    label: 'Asset Manager',
    description: 'Run the asset register: assign, transfer, maintain, audit and retire assets',
  },
  department_head: {
    label: 'Department Head',
    description:
      "A Reporting Manager's permissions across a whole department. Granted automatically from the org chart",
  },
  helpdesk_manager: {
    label: 'Helpdesk Manager',
    description:
      'Every helpdesk ticket including confidential categories, helpdesk configuration and reports',
  },
  helpdesk_agent: {
    label: 'Helpdesk Agent',
    description:
      "Works tickets in their helpdesk teams' categories. Granted automatically from team membership",
  },
  travel_desk_manager: {
    label: 'Travel Desk Manager',
    description:
      'Runs the travel desk: desk teams, coverage, vendors, every booking queue, and reads every trip',
  },
  travel_desk_agent: {
    label: 'Travel Desk Admin',
    description:
      "Books travel in their travel desk teams' queues. Granted automatically from team membership",
  },
});
